/* eslint-disable */
/**
 * offlineSearch.service.js
 * Offline-first search engine that indexes and queries cached playlists, songs, artists,
 * favorites, and CacheStorage (triad-content-cache) with fuzzy matching and relevance scoring.
 */

import dbService from '@/service/db.service';
import playlistStore from '@/store/playlist.store';
import sidemenuStore from '@/store/sidemenu.store';
import userStore from '@/store/user.store';

class OfflineSearchService {
    constructor() {
        this.cacheTimestamp = 0;
        this.indexedItems = {
            songs: new Map(),
            playlists: new Map(),
            artists: new Map()
        };
    }

    normalize(text) {
        if (!text) return '';
        return String(text)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^\w\s]/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    getTokens(text) {
        const norm = this.normalize(text);
        return norm ? norm.split(' ').filter(Boolean) : [];
    }

    /**
     * Harvests all offline data across stores, localStorage/IndexedDB, and Service Worker CacheStorage
     */
    async collectOfflineData() {
        const songsMap = new Map();
        const playlistsMap = new Map();
        const artistsMap = new Map();

        const registerArtist = (artist) => {
            if (!artist) return;
            const name = typeof artist === 'string' ? artist : artist.name;
            if (!name) return;
            const key = this.normalize(name);
            if (key && !artistsMap.has(key)) {
                artistsMap.set(key, {
                    _id: artist._id || artist.id || `artist_${key}`,
                    id: artist._id || artist.id || `artist_${key}`,
                    name: name,
                    type: 'ARTIST'
                });
            }
        };

        const registerSong = (rawSong) => {
            if (!rawSong) return;
            const song = rawSong.data || rawSong;
            const songId = song._id || song.id || rawSong.songId || rawSong.song_id || rawSong.song;
            if (!songId || !song.title) return;

            const existing = songsMap.get(songId);
            const artistObj = typeof song.artist === 'string' ? { name: song.artist } : (song.artist || (existing && existing.artist));
            if (artistObj) registerArtist(artistObj);

            const lyricContent = song.modifiedLyric || rawSong.modifiedLyric || song.content || song.lyric || rawSong.content || rawSong.lyric || (existing && existing.lyric) || '';

            const songEntry = {
                _id: songId,
                id: songId,
                title: song.title,
                name: song.title,
                type: 'SONG',
                artist: artistObj,
                lyric: lyricContent,
                content: lyricContent,
                modifiedLyric: lyricContent,
                tags: song.tags || (existing && existing.tags) || [],
                tone: song.tone || song.chord || rawSong.chord || (existing && existing.tone) || '',
                isPublic: song.isPublic || song.is_public || rawSong.isPublic || rawSong.is_public || false
            };

            songsMap.set(songId, songEntry);
            if (song._id && song._id !== songId) songsMap.set(song._id, songEntry);
            if (song.id && song.id !== songId) songsMap.set(song.id, songEntry);
            if (rawSong._id && rawSong._id !== songId) songsMap.set(rawSong._id, songEntry);
            if (rawSong.id && rawSong.id !== songId) songsMap.set(rawSong.id, songEntry);
        };

        const registerPlaylist = (rawPl) => {
            if (!rawPl) return;
            const pl = rawPl.data || rawPl;
            const id = pl._id || pl.id;
            if (!id || (!pl.name && !pl.title)) return;

            const name = pl.name || pl.title;
            const songs = Array.isArray(pl.songs) ? pl.songs : [];

            playlistsMap.set(id, {
                _id: id,
                id: id,
                name: name,
                title: name,
                type: 'PLAYLIST',
                songs: songs
            });

            // Extract songs inside the playlist
            for (const item of songs) {
                registerSong(item);
            }
        };

        // 1. Harvest from Vuex stores
        try {
            if (sidemenuStore?.state?.playlists) {
                sidemenuStore.state.playlists.forEach(registerPlaylist);
            }
            if (playlistStore?.state?.listPlaylists) {
                playlistStore.state.listPlaylists.forEach(registerPlaylist);
            }
            if (playlistStore?.state?.playlistsOwnedBy) {
                playlistStore.state.playlistsOwnedBy.forEach(registerPlaylist);
            }
            if (playlistStore?.state?.playlist) {
                registerPlaylist(playlistStore.state.playlist);
            }
            try {
                const { default: searchModalStore } = await import('@/store/search.modal.store');
                if (searchModalStore?.state?.favorites) {
                    searchModalStore.state.favorites.forEach(registerSong);
                }
            } catch (_) {}
        } catch (e) {
            console.warn('[OfflineSearch] Error harvesting from stores:', e);
        }

        // 2. Harvest from dbService & localStorage
        try {
            const userId = userStore?.state?.userInfo?.id || 'anon';
            const userFavs = await dbService.getItem(`favorites-user-${userId}`);
            if (Array.isArray(userFavs)) {
                userFavs.forEach(registerSong);
            }
            const generalFavs = await dbService.getItem('favorites');
            if (Array.isArray(generalFavs)) {
                generalFavs.forEach(registerSong);
            }
        } catch (e) {
            console.warn('[OfflineSearch] Error harvesting from dbService:', e);
        }

        // 3. Harvest from Service Worker CacheStorage
        if (typeof caches !== 'undefined') {
            try {
                const cacheNames = ['triad-content-cache', 'triad-api-cache'];
                for (const cacheName of cacheNames) {
                    try {
                        const cache = await caches.open(cacheName);
                        const requests = await cache.keys();
                        for (const req of requests) {
                            const url = new URL(req.url);
                            if (url.pathname.startsWith('/api/song/') || url.pathname.startsWith('/api/playlist') || url.pathname === '/api/user/favorite-songs') {
                                try {
                                    const response = await cache.match(req);
                                    if (response) {
                                        const data = await response.clone().json();
                                        if (Array.isArray(data)) {
                                            if (url.pathname.startsWith('/api/playlist')) {
                                                data.forEach(registerPlaylist);
                                            } else {
                                                data.forEach(registerSong);
                                            }
                                        } else if (data && typeof data === 'object') {
                                            if (url.pathname.startsWith('/api/playlist')) {
                                                registerPlaylist(data);
                                            } else if (data.title || data.data?.title) {
                                                registerSong(data);
                                            }
                                        }
                                    }
                                } catch (err) {
                                    // Non-JSON or corrupt response in cache, ignore safely
                                }
                            }
                        }
                    } catch (err) {
                        // Cache name not found or access denied
                    }
                }
            } catch (e) {
                console.warn('[OfflineSearch] Error scanning CacheStorage:', e);
            }
        }

        this.indexedItems = {
            songs: songsMap,
            playlists: playlistsMap,
            artists: artistsMap
        };
        this.cacheTimestamp = Date.now();
    }

    calculateScore(item, query, queryTokens) {
        let score = 0;
        const normQuery = this.normalize(query);
        if (!normQuery) return 0;

        if (item.type === 'SONG') {
            const titleNorm = this.normalize(item.title);
            const artistNorm = item.artist?.name ? this.normalize(item.artist.name) : '';
            const lyricNorm = this.normalize(item.lyric);
            const tagsNorm = Array.isArray(item.tags) ? item.tags.map(t => this.normalize(typeof t === 'string' ? t : t.name)).join(' ') : '';
            const toneNorm = this.normalize(item.tone);

            // Title matches (highest priority)
            if (titleNorm === normQuery) {
                score += 150;
            } else if (titleNorm.startsWith(normQuery)) {
                score += 90;
            } else if (titleNorm.includes(normQuery)) {
                score += 60;
            } else {
                let tokenMatches = 0;
                for (const t of queryTokens) {
                    if (titleNorm.includes(t)) tokenMatches++;
                }
                if (tokenMatches === queryTokens.length) {
                    score += 45;
                } else if (tokenMatches > 0) {
                    score += tokenMatches * 15;
                }
            }

            // Artist matches
            if (artistNorm) {
                if (artistNorm === normQuery) {
                    score += 80;
                } else if (artistNorm.startsWith(normQuery)) {
                    score += 50;
                } else if (artistNorm.includes(normQuery)) {
                    score += 35;
                } else {
                    for (const t of queryTokens) {
                        if (artistNorm.includes(t)) score += 10;
                    }
                }
            }

            // Tags matches
            if (tagsNorm) {
                if (tagsNorm.includes(normQuery)) {
                    score += 30;
                } else {
                    for (const t of queryTokens) {
                        if (tagsNorm.includes(t)) score += 8;
                    }
                }
            }

            // Tone / Chord match
            if (toneNorm && toneNorm.includes(normQuery)) {
                score += 20;
            }

            // Lyrics match
            if (lyricNorm && lyricNorm.includes(normQuery)) {
                score += 25;
            }
        } else if (item.type === 'PLAYLIST') {
            const nameNorm = this.normalize(item.name || item.title);
            if (nameNorm === normQuery) {
                score += 130;
            } else if (nameNorm.startsWith(normQuery)) {
                score += 80;
            } else if (nameNorm.includes(normQuery)) {
                score += 50;
            } else {
                let tokenMatches = 0;
                for (const t of queryTokens) {
                    if (nameNorm.includes(t)) tokenMatches++;
                }
                if (tokenMatches > 0) {
                    score += tokenMatches * 15;
                }
            }
        } else if (item.type === 'ARTIST') {
            const nameNorm = this.normalize(item.name);
            if (nameNorm === normQuery) {
                score += 120;
            } else if (nameNorm.startsWith(normQuery)) {
                score += 75;
            } else if (nameNorm.includes(normQuery)) {
                score += 45;
            } else {
                for (const t of queryTokens) {
                    if (nameNorm.includes(t)) score += 12;
                }
            }
        }

        return score;
    }

    /**
     * Executes fuzzy search query against all cached data
     * @param {string} query
     * @param {object} options { page: 0, limit: 20 }
     * @returns {Promise<Array>}
     */
    async search(query, options = {}) {
        if (!query || typeof query !== 'string' || query.trim().length === 0) {
            return [];
        }

        // Re-harvest if cache is older than 10 seconds or empty
        if (Date.now() - this.cacheTimestamp > 10000 || this.indexedItems.songs.size === 0) {
            await this.collectOfflineData();
        }

        const queryTokens = this.getTokens(query);
        if (queryTokens.length === 0) return [];

        const results = [];
        const seenSongIds = new Set();

        // Check Songs (deduplicating multiple alias keys)
        for (const song of this.indexedItems.songs.values()) {
            if (seenSongIds.has(song._id)) continue;
            seenSongIds.add(song._id);

            const score = this.calculateScore(song, query, queryTokens);
            if (score > 0) {
                results.push({ item: song, score });
            }
        }

        // Check Playlists
        for (const pl of this.indexedItems.playlists.values()) {
            const score = this.calculateScore(pl, query, queryTokens);
            if (score > 0) {
                results.push({ item: pl, score });
            }
        }

        // Check Artists
        for (const artist of this.indexedItems.artists.values()) {
            const score = this.calculateScore(artist, query, queryTokens);
            if (score > 0) {
                results.push({ item: artist, score });
            }
        }

        // Sort descending by score
        results.sort((a, b) => b.score - a.score);

        const page = options.page || 0;
        const limit = options.limit || 20;
        const startIndex = page * limit;
        const pagedResults = results.slice(startIndex, startIndex + limit).map(r => r.item);

        return pagedResults;
    }
}

export const offlineSearchService = new OfflineSearchService();
export default offlineSearchService;
